import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { AuthService } from '@auth0/auth0-angular';
import { Subject, combineLatest, filter, takeUntil, tap } from 'rxjs';
import { HierarchyType, Site } from '../../models/hierarchy.model';
import {
  Tenant,
  TenantItem,
  TenantRegion,
  TenantSite
} from '../../models/tenant.model';
import { FlagService } from '../../services/flag.service';
import { HierarchyService } from '../../services/hierarchy.service';
import { TenantService } from '../../services/tenant.service';
import { UserInfoService } from '../../services/user-info.service';

@Component({
  selector: 'tenant-menu',
  standalone: false,
  templateUrl: './tenant-menu.component.html',
  styleUrls: ['./tenant-menu.component.sass']
})
export class TenantMenuComponent implements OnInit, OnDestroy {
  @ViewChild('menuTrigger') trigger: any;
  public showModal = false;
  private ngUnsubscribe = new Subject<void>();
  changingTenantSite = false;
  tenants: Tenant[] = [];
  filteredTenants: Tenant[] = [];
  selectedTenant: Tenant;
  selectedTenantSite: TenantSite;
  selectedTenantRegion: TenantRegion;
  public searchControl: FormControl = new FormControl('');
  searchPlaceholder = $localize`Search`;
  activeSiteIds = new Set<string>();
  inactiveSiteIds = new Set<string>();

  constructor(
    public tenantService: TenantService,
    public hierarchyService: HierarchyService,
    private dialog: MatDialog,
    private authService: AuthService,
    public flagService: FlagService,
    public userInfoService: UserInfoService
  ) {}

  ngOnInit() {
    combineLatest([
      this.tenantService.tenants$,
      this.tenantService.selectedTenant$,
      this.hierarchyService.selectedSite$
    ])
      .pipe(
        filter(([tenants, site]) => !!tenants.length && !!site.id.length),
        tap(([tenants, selectedTenant, selectedSite]) => {
          const sites =
            (this.hierarchyService.queryAllOfLevel(
              HierarchyType.Site
            ) as Site[]) ?? [];
          const hideDeactivatedSites =
            !this.userInfoService.hasPermission('platformSupport');

          this.inactiveSiteIds = new Set(
            sites
              .filter((site) => site.inactive === true)
              .map((site) => site.id)
          );

          this.activeSiteIds = new Set(
            sites
              .filter((site) => !hideDeactivatedSites || site.inactive !== true)
              .map((site) => site.id)
          );

          for (const tenant of tenants) {
            const unassignedInx = tenant.regions.findIndex(
              (r) => r.name === 'Unassigned'
            );

            if (tenant.regions[unassignedInx]) {
              tenant.regions[unassignedInx].name =
                $localize`Default Region` as string;
              tenant.regions?.sort((a, b) => a?.name.localeCompare(b?.name));

              const defaultRegionIdx = tenant.regions.findIndex(
                (r) => r.name === $localize`Default Region`
              );
              tenant.regions[defaultRegionIdx].name = 'Unassigned';
            }
          }

          this.tenants = tenants;
          this.selectedTenant = selectedTenant;

          const selectedTenantRegion = this.selectedTenant.regions.find(
            (region) => region.id === selectedSite.regionId
          );
          const selectedTenantSite = this.selectedTenant.regions
            .flatMap((region) => region.sites)
            .find((site) => site.id === selectedSite.id);

          if (selectedTenantRegion) {
            this.selectedTenantRegion = selectedTenantRegion;
          }

          if (selectedTenantSite) {
            this.selectedTenantSite = selectedTenantSite;
          }

          this.resetExpansion();
        }),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe();

    this.searchControl.valueChanges
      .pipe(
        takeUntil(this.ngUnsubscribe),
        tap(() => this.filterTenantList())
      )
      .subscribe();
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  showMenu() {
    this.trigger?.openMenu();
  }

  hideMenu() {
    this.searchControl.setValue('');
    this.trigger?.closeMenu();
  }

  scrollToCurrentSite(): void {
    const currentSite = document.getElementsByClassName('selectedSite');
    if (currentSite[0]) {
      // Scroll to the second active element
      currentSite[0].scrollIntoView({
        block: 'center'
      });
    }
  }

  toggleItem(item: TenantItem) {
    switch (item.type) {
      case HierarchyType.Company: {
        const { regions } = item as Tenant;
        regions.forEach(
          (region) => (region.meta['isVisible'] = !region.meta['isVisible'])
        );

        if (regions.length === 1) {
          this.toggleItem(regions[0]);
        }
        break;
      }
      case HierarchyType.Region:
        (item as TenantRegion).sites.forEach(
          (site) => (site.meta['isVisible'] = !site.meta['isVisible'])
        );
        break;
    }
  }

  resetExpansion() {
    for (const tenant of this.tenants) {
      tenant.meta['isVisible'] = true;

      for (const region of tenant.regions) {
        region.meta['isVisible'] = tenant.id === this.selectedTenant.id;

        for (const site of region.sites) {
          site.meta['isVisible'] =
            region.id === this.selectedTenantRegion.id &&
            this.isSiteAvailableToSelect(tenant.id, site);
        }
      }
    }
  }

  selectSite(tenant: Tenant, tenantSite: TenantSite) {
    this.hideMenu();
    if (
      this.selectedTenant?.id === tenant.id &&
      this.selectedTenantSite?.id === tenantSite.id
    ) {
      return;
    }

    this.changingTenantSite = true;

    this.tenantService
      .patchSelectedTenantSite(tenant.id, tenantSite.id, tenant.orgId)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe();
  }

  filterTenantList() {
    const searchText = (this.searchControl.value as string).toLowerCase();
    if (!searchText.length) {
      this.resetExpansion();

      return;
    }

    const matchesFilter = (name: string) =>
      name.toLowerCase().includes(searchText);

    for (const tenant of this.tenants) {
      tenant.meta['isVisible'] = matchesFilter(tenant.name);

      for (const region of tenant.regions) {
        region.meta['isVisible'] = matchesFilter(region.name);

        if (region.meta['isVisible']) {
          tenant.meta['isVisible'] = true;
        }

        for (const site of region.sites) {
          site.meta['isVisible'] =
            this.isSiteAvailableToSelect(tenant.id, site) &&
            matchesFilter(site.name);

          if (site.meta['isVisible']) {
            tenant.meta['isVisible'] = region.meta['isVisible'] = true;
          }
        }
      }
    }
  }

  isSiteAvailableToSelect(tenantId: string, site: TenantSite): boolean {
    if (this.userInfoService.hasPermission('platformSupport')) {
      return true;
    }

    if (typeof site.inactive === 'boolean') {
      return site.inactive !== true;
    }

    if (tenantId !== this.selectedTenant?.id) {
      return true;
    }

    return this.activeSiteIds.has(site.id);
  }

  isSiteDeactivated(site?: TenantSite): boolean {
    if (!site) {
      return false;
    }

    return site.inactive === true || this.inactiveSiteIds.has(site.id);
  }
}
